/**
 * useRqcShipments — RQC 출하 데이터 hook.
 *
 * 동작:
 *   - selectedDate 변경 시 fetchOnDemand 1회 실행
 *   - selectedDate === KST 오늘 일 때만 1분 폴링 (탭 포커스 상태와 무관)
 *
 * 의도적 단순화: 탭 포커스 들어올 때마다 새로고침을 발생시키지 않음.
 * 사용자가 여러 탭을 왕복할 때 RQC 서버에 부하가 몰리는 걸 방지하기 위함.
 * 백그라운드 탭의 setInterval은 브라우저가 자체적으로 throttle.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { rqcRepo } from "@/services/rqc.repository";
import { kstToday, type RqcShipment } from "@/domain/rqc";

const POLL_INTERVAL_MS = 1 * 60 * 1000;

export interface UseRqcShipmentsState {
  shipments: RqcShipment[];
  loading: boolean;
  mode: "live" | "cached" | "idle";
  error: string | null;
  /** ko-KR 시각 — 마지막 갱신 시점 */
  lastUpdated: string;
  /** 자료 시점(상대시각 표시용) — 라이브 = 받은 시각, 캐시 = 캐시가 마지막으로 저장된 시각 */
  lastUpdatedAt: Date | null;
  /** 수동 새로고침 */
  refresh: () => Promise<void>;
}

export function useRqcShipments(selectedDate: string): UseRqcShipmentsState {
  const [shipments, setShipments] = useState<RqcShipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"live" | "cached" | "idle">("idle");
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState("");
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);

  const fetchingRef = useRef(false);
  // 요청 세대 번호 — 응답이 돌아왔을 때 "아직 최신 요청인지" 검증용.
  // 날짜를 바꾸면 이 값을 올려서 진행 중이던 옛 조회의 결과를 버린다(stale 잔상 방지).
  const reqIdRef = useRef(0);
  const pollTimerRef = useRef<number | null>(null);

  const fetchData = useCallback(async () => {
    if (!selectedDate) return;
    // 같은 날짜에서 폴링/중복 호출이 겹쳐 RQC에 몰리는 것만 차단.
    // (날짜 변경은 아래 effect가 fetchingRef를 풀어주므로 이 가드에 막히지 않는다)
    if (fetchingRef.current) return;
    const myReq = ++reqIdRef.current;
    fetchingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      // 최소 200ms 골격 표시 시간 보장 — fetch가 너무 빨라도 시퀀스 첫 단계가 보이게
      const minDelay = new Promise<void>((r) => setTimeout(r, 200));
      const [result] = await Promise.all([
        rqcRepo.fetchOnDemand(selectedDate),
        minDelay,
      ]);
      // 응답이 도착한 사이 더 새 요청(날짜 변경 등)이 있었으면 이 결과는 버린다.
      if (myReq !== reqIdRef.current) return;
      setShipments(result.shipments);
      setMode(result.mode);
      setLastUpdated(result.fetchedAt);
      setLastUpdatedAt(result.dataAt);
      if (result.error) setError(result.error);
    } catch (e) {
      if (myReq !== reqIdRef.current) return;
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      fetchingRef.current = false;
      // 내가 최신 요청일 때만 로딩 해제 — 옛 조회가 뒤늦게 끝나도 로딩 상태 유지
      if (myReq === reqIdRef.current) setLoading(false);
    }
  }, [selectedDate]);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    if (selectedDate === kstToday()) {
      pollTimerRef.current = window.setInterval(fetchData, POLL_INTERVAL_MS);
    }
  }, [selectedDate, fetchData, stopPolling]);

  // 날짜 변경 시 1회 fetch + 폴링 재설정
  useEffect(() => {
    // 옛 날짜 데이터가 잠깐 보이는 잔상 방지 — 즉시 비움
    setShipments([]);
    setMode("idle");
    // 진행 중이던 옛 날짜 조회를 stale로 표시(응답이 와도 버려짐) +
    // 중복 가드를 풀어서 새 날짜 조회가 막히지 않게 한다.
    reqIdRef.current++;
    fetchingRef.current = false;
    fetchData();
    startPolling();
    return stopPolling;
  }, [fetchData, startPolling, stopPolling]);

  return { shipments, loading, mode, error, lastUpdated, lastUpdatedAt, refresh: fetchData };
}
